import { Link } from "react-router-dom";
import {
  ArrowRight,
  HelpCircle,
  Inbox,
  Layers3,
  PanelTop,
  PhoneCall,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/app/components/ui/utils";

type ConsoleTile = {
  href: string;
  title: string;
  kicker: "GENERAL" | "CALLER";
  /** Role accent uses console-mode role tokens via inline style. */
  accent: "blue" | "teal" | "orange";
  icon: LucideIcon;
  blurb: string;
};

const ROLE_BG: Record<ConsoleTile["accent"], string> = {
  blue: "#3B82F6",
  teal: "#14B8A6",
  orange: "#F97316",
};

const TILES: ConsoleTile[] = [
  {
    href: "/console/dashboard",
    title: "工作台",
    kicker: "GENERAL",
    accent: "blue",
    icon: PanelTop,
    blurb: "4 进程健康灯 · 4 步上手清单",
  },
  {
    href: "/console/catalog",
    title: "热线目录",
    kicker: "CALLER",
    accent: "teal",
    icon: Layers3,
    blurb: "唯一发起调用入口 · TryCallDrawer",
  },
  {
    href: "/console/calls",
    title: "调用记录",
    kicker: "CALLER",
    accent: "teal",
    icon: PhoneCall,
    blurb: "人话通话日志 · 摘要 / 背景 / 结果",
  },
  {
    href: "/console/approvals",
    title: "审批中心",
    kicker: "CALLER",
    accent: "teal",
    icon: Inbox,
    blurb: "5 filter tab · 审批疲劳 banner",
  },
  {
    href: "/console/access-lists",
    title: "名单管理",
    kicker: "CALLER",
    accent: "teal",
    icon: ShieldCheck,
    blurb: "Responder / Hotline 白 + 黑名单",
  },
  {
    href: "/console/help",
    title: "上手 & 帮助",
    kicker: "GENERAL",
    accent: "blue",
    icon: HelpCircle,
    blurb: "sticky TOC · 8 篇内置文章",
  },
];

export function PlaygroundConsoleSection() {
  return (
    <div>
      <div className="border-l-8 border-black pl-4 mb-10">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-1">SECTION H</p>
        <h2 className="text-3xl font-black uppercase">CONSOLE MODE 原型</h2>
      </div>

      <div className="border-4 border-black bg-white p-6 sm:p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div className="max-w-2xl">
            <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-1">
              【H.1】Delegated-Execution Console · Prototype
            </p>
            <h3 className="text-2xl font-black uppercase">在 brand 体系内的「干活方言」</h3>
            <p className="font-bold text-zinc-600 mt-3 text-sm leading-relaxed">
              Console mode 是品牌设计语言的功能子方言：保留 Space Grotesk 字体、品牌语义色、轻量粗野动效；但换上
              密度优先的 4px 圆角、paper 米色基底、canonical 状态色 token。
            </p>
            <p className="font-bold text-zinc-600 mt-2 text-sm leading-relaxed">
              <span className="bg-[#FACC15] px-1.5 py-0.5 text-black">本原型不代替业务逻辑</span>
              。每个 region 的「为什么存在 / 服务什么操作 / 数据来源」都写在{" "}
              <code className="font-mono text-[12px] bg-zinc-100 px-1.5 py-0.5">docs/console-content-spec.md</code>
              ，落地任何一页前必须先读。
            </p>
          </div>
          <Link
            to="/console"
            className="inline-flex items-center gap-2 border-4 border-black bg-[#A3E635] px-6 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] shrink-0"
          >
            打开原型 hub
            <ArrowRight className="h-4 w-4" strokeWidth={3} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.href}
                to={t.href}
                className={cn(
                  "block border-4 border-black bg-[#F7F2E8] p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]",
                  "transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]",
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="inline-flex items-center justify-center h-8 w-8 border-2 border-black text-white"
                    style={{ backgroundColor: ROLE_BG[t.accent] }}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  <span className="text-[10px] font-black tracking-widest text-zinc-500">
                    {t.kicker}
                  </span>
                </div>
                <p className="text-base font-black text-black">{t.title}</p>
                <p className="text-[11px] font-bold text-zinc-500 mt-1.5 leading-snug">
                  {t.blurb}
                </p>
                <p className="text-[11px] font-bold text-zinc-700 mt-3 inline-flex items-center gap-1">
                  打开原型
                  <ArrowRight className="h-3 w-3" strokeWidth={3} />
                </p>
              </Link>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t-4 border-black/10 flex flex-wrap items-center gap-3 text-xs font-bold text-zinc-600">
          <span>规范源：</span>
          <code className="font-mono text-[11px] bg-zinc-100 px-2 py-1 rounded">
            src/styles/delexec-console-tokens.css
          </code>
          <code className="font-mono text-[11px] bg-zinc-100 px-2 py-1 rounded">
            src/styles/console-mode.css
          </code>
          <code className="font-mono text-[11px] bg-zinc-100 px-2 py-1 rounded">
            src/design-system/shells/console-shell.tsx
          </code>
        </div>
      </div>
    </div>
  );
}
